import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Clock, RefreshCw, LogOut } from 'lucide-react';

interface SessionWarningDialogProps {
  open: boolean;
  timeLeft: number; // in milliseconds
  onExtend: () => void;
  onLogout: () => void;
  onDismiss: () => void;
}

const SessionWarningDialog: React.FC<SessionWarningDialogProps> = ({
  open,
  timeLeft,
  onExtend,
  onLogout,
  onDismiss,
}) => {
  const [countdown, setCountdown] = useState(timeLeft);

  useEffect(() => {
    if (!open) return;
    setCountdown(timeLeft);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1000) {
          onLogout(); // Auto-logout when countdown reaches 0
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [open, timeLeft, onLogout]);

  const minutes = Math.floor(countdown / 60000);
  const seconds = Math.floor((countdown % 60000) / 1000);

  return (
    <Dialog open={open} onOpenChange={onDismiss}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-500" />
            Session Expiring Soon
          </DialogTitle>
          <DialogDescription>
            Your session will expire in {minutes}:{seconds.toString().padStart(2, '0')} minutes.<br />
            Please save your work and choose an option below.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 mt-4">
          <Button onClick={onExtend} className="w-full">
            <RefreshCw className="h-4 w-4 mr-2" />
            Extend Session
          </Button>
          <Button variant="outline" onClick={onLogout} className="w-full">
            <LogOut className="h-4 w-4 mr-2" />
            Logout Now
          </Button>
          <Button variant="ghost" onClick={onDismiss} className="w-full">
            Continue Working
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SessionWarningDialog; 